import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { certification } from "../../../static/data";
import { SectionSubtitle, SectionTitle } from "../../SectionTitleElements";
import { CertificationSEO } from "../../SEO";
import {
  CertButton,
  CertCard,
  CertContainer,
  CertImg,
  CertSection,
  CertTitle,
} from "./CertificationElements";

const Certification = ({ darkMode }) => {
  const [open, setOpen] = useState(false);
  const [selectedCert, setSelectedCert] = useState(null);
  const [page, setPage] = useState(1);
  const itemsPerPage = 4;
  const [isAnimating, setIsAnimating] = useState(false);

  const handleOpenModal = (cert) => {
    setSelectedCert(cert);
    setOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedCert(null);
    setOpen(false);
  };

  // Calculate pagination variables
  const totalPages = Math.ceil(certification.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const paginatedData = certification.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Handle pagination actions
  const handleNextPage = () => {
    if (page < totalPages && !isAnimating) {
      setIsAnimating(true);
      setPage(page + 1);
      // Scroll to top of the section when changing pages
      const element = document.getElementById("certification");
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  const handlePrevPage = () => {
    if (page > 1 && !isAnimating) {
      setIsAnimating(true);
      setPage(page - 1);
      // Scroll to top of the section when changing pages
      const element = document.getElementById("certification");
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  // Reset animation state after animation completes
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsAnimating(false);
    }, 600);
    return () => clearTimeout(timer);
  }, [page]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    enter: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };

  return (
    <>
      <CertSection id="certification">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
          className="w-full"
        >
          <SectionSubtitle>My Achievements</SectionSubtitle>
          <SectionTitle>Certifications</SectionTitle>

          {/* Conditional rendering for empty certifications */}
          {certification.length === 0 && (
            <h4 className="p-3 text-sm text-red-700 font-medium">
              No certifications found.
            </h4>
          )}

          <AnimatePresence mode="wait">
            <motion.div
              key={page}
              initial="initial"
              animate="enter"
              exit="exit"
              variants={pageVariants}
              transition={{ duration: 0.5 }}
            >
              <CertContainer>
                {paginatedData.map((cert) => (
                  <motion.div key={cert.id} variants={itemVariants}>
                    <CertCard
                      light={darkMode ? 1 : 0}
                      className={`${darkMode ? "dark" : ""}`}
                    >
                      <div className="overflow-hidden rounded-lg">
                        <CertImg
                          src={cert.certifications_image}
                          alt={cert.certifications_title}
                        />
                      </div>
                      <CertTitle light={darkMode ? 1 : 0}>
                        {cert.certifications_title}
                      </CertTitle>

                      <div className="flex mt-auto px-1">
                        <CertButton
                          href={cert.certifications_link}
                          target="_blank"
                          className="flex items-center gap-2"
                        >
                          <span>View Certificate</span>
                          <i className="bx bx-link-external text-sm"></i>
                        </CertButton>
                        <CertButton
                          className="btn_ghost flex items-center gap-2"
                          onClick={() => handleOpenModal(cert)}
                        >
                          <span>Details</span>
                          <i className="bx bx-info-circle text-sm"></i>
                        </CertButton>
                      </div>
                    </CertCard>
                  </motion.div>
                ))}
              </CertContainer>
            </motion.div>
          </AnimatePresence>

          {/* Pagination controls */}
          {totalPages > 1 && (
            <motion.div
              variants={itemVariants}
              className="flex items-center justify-center mt-8 mb-4"
            >
              <button
                className={`flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium transition-all duration-300
                  ${
                    page === 1 || isAnimating
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                      : "bg-red-600 text-white hover:bg-red-700 hover:shadow-md"
                  }`}
                onClick={handlePrevPage}
                disabled={page === 1 || isAnimating}
                aria-label="Previous page"
              >
                <i className="bx bx-chevron-left mr-1 text-lg"></i>
                <span>Prev</span>
              </button>

              <div className="mx-4 text-sm font-medium px-4 py-2 bg-white shadow rounded-md">
                {`Page ${page} of ${totalPages}`}
              </div>

              <button
                className={`flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium transition-all duration-300
                  ${
                    page === totalPages || isAnimating
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                      : "bg-accent text-white hover:bg-secondary hover:shadow-md"
                  }`}
                onClick={handleNextPage}
                disabled={page === totalPages || isAnimating}
                aria-label="Next page"
              >
                <span>Next</span>
                <i className="bx bx-chevron-right ml-1 text-lg"></i>
              </button>
            </motion.div>
          )}
        </motion.div>
      </CertSection>

      {/* Modal for certification details with certification-specific SEO */}
      {selectedCert && (
        <>
          {/* Certification-specific SEO */}
          <CertificationSEO certificationId={selectedCert.id} />

          <div
            className={`fixed z-50 inset-0 h-screen flex justify-center items-center transition-colors ${
              open ? "visible bg-black/40" : "invisible"
            }`}
            onClick={handleCloseModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className={`max-h-[80vh] w-full max-w-4xl mx-3 md:mx-6 bg-white rounded-xl overflow-hidden shadow-xl ${
                darkMode ? "dark:bg-[#281F1FFF] dark:text-gray-200" : ""
              }`}
            >
              <div className="relative p-6">
                <button
                  onClick={handleCloseModal}
                  className="absolute top-4 right-4 p-2 rounded-full text-gray-400 bg-gray-100 hover:bg-gray-200 hover:text-gray-600 transition-all"
                  aria-label="Close certification details"
                >
                  <i className="bx bx-x text-xl"></i>
                </button>

                <div className="mb-6 border-b pb-4 dark:border-gray-700">
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-800">
                    {selectedCert.certifications_title}
                  </h3>
                </div>

                <div className="flex flex-col md:flex-row gap-6">
                  <div className="md:w-1/2">
                    <div className="rounded-lg overflow-hidden shadow-lg">
                      <img
                        src={selectedCert.certifications_image}
                        alt={selectedCert.certifications_title}
                        className="w-full h-auto object-cover"
                      />
                    </div>
                  </div>

                  <div
                    className="md:w-1/2 overflow-y-auto"
                    style={{ maxHeight: "300px" }}
                  >
                    <div
                      className="prose prose-sm max-w-none dark:prose-invert"
                      dangerouslySetInnerHTML={{
                        __html: selectedCert.certifications_desc,
                      }}
                    ></div>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t dark:border-gray-700 flex justify-end">
                  <a
                    href={selectedCert.certifications_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-accent text-white rounded-md hover:bg-secondary transition-colors"
                  >
                    View Certificate
                    <i className="bx bx-link-external ml-2"></i>
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </>
  );
};

export default Certification;
